import React, { useEffect, useMemo, useState, useCallback } from "react";
import { compassApi } from "@/lib/compassApi";
import { contactsApi, organizationsApi } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Sparkles, Plus, Compass, GripVertical, Trash2, ChevronDown, ChevronUp,
  Wand2, ShieldAlert, CircleAlert, CheckCircle2, HelpCircle, ArrowRight,
  Zap,
} from "lucide-react";

/** Reusable recommendation pill colors — matches decision statuses spec §2. */
const REC_STYLE = {
  "Strong Fit":        "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Potential Fit":     "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Needs More Evidence": "bg-amber-500/15 text-amber-200 border-amber-500/30",
  "Weak Fit":          "bg-orange-500/15 text-orange-200 border-orange-500/30",
  "Do Not Proceed":    "bg-red-500/20 text-red-300 border-red-500/30",
};

function RecPill({ rec, testid }) {
  const cls = REC_STYLE[rec] || "bg-white/5 text-white/60 border-white/10";
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider ${cls}`}
    >
      {rec || "—"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Framework Builder Dialog
// ---------------------------------------------------------------------------
function FrameworkBuilderDialog({ open, onClose, projectId, framework, onSaved }) {
  const [form, setForm] = useState({
    name: "", description: "", target_type: "contact",
    criteria: [], disqualifiers: [],
    decision_thresholds: { strong_fit: 80, potential_fit: 60, weak_fit: 40 },
    status: "Draft",
  });

  useEffect(() => {
    if (framework) setForm(framework);
    else setForm({
      name: "", description: "", target_type: "contact",
      criteria: [
        { id: `c-${Math.random().toString(36).slice(2, 8)}`, name: "", description: "", weight: 1, must_have: false, red_flag: false, evidence_expectations: [] }
      ],
      disqualifiers: [],
      decision_thresholds: { strong_fit: 80, potential_fit: 60, weak_fit: 40 },
      status: "Draft",
    });
  }, [framework, open]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const addCrit = () => set({ criteria: [...form.criteria, { id: `c-${Math.random().toString(36).slice(2, 8)}`, name: "", description: "", weight: 1, must_have: false, red_flag: false, evidence_expectations: [] }] });
  const rmCrit = (i) => set({ criteria: form.criteria.filter((_, k) => k !== i) });
  const setCrit = (i, patch) => set({ criteria: form.criteria.map((c, k) => k === i ? { ...c, ...patch } : c) });
  const addDisq = () => set({ disqualifiers: [...form.disqualifiers, { id: `d-${Math.random().toString(36).slice(2, 8)}`, name: "", description: "" }] });
  const rmDisq = (i) => set({ disqualifiers: form.disqualifiers.filter((_, k) => k !== i) });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Framework needs a name");
    if (!form.criteria?.length) return toast.error("Add at least one criterion");
    try {
      const payload = { ...form };
      let saved;
      if (framework?.id) saved = (await compassApi.updateFramework(framework.id, payload)).data;
      else saved = (await compassApi.createFramework(projectId, payload)).data;
      toast.success(`Framework ${framework ? "updated" : "created"}`);
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="compass-framework-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Compass className="w-4 h-4" />
            {framework ? "Edit Framework" : "New Evaluation Framework"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input data-testid="compass-fw-name" value={form.name}
                     onChange={(e) => set({ name: e.target.value })}
                     placeholder="e.g. Discovery Agent Candidate Evaluation" />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea data-testid="compass-fw-description" value={form.description || ""}
                        onChange={(e) => set({ description: e.target.value })} rows={2} />
            </div>
            <div>
              <Label>Evaluation target</Label>
              <Select value={form.target_type} onValueChange={(v) => set({ target_type: v })}>
                <SelectTrigger data-testid="compass-fw-target-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contact">Contact / Person</SelectItem>
                  <SelectItem value="organization">Organization</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="intake_item">Intake item</SelectItem>
                  <SelectItem value="idea">Idea / Strategic option</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set({ status: v })}>
                <SelectTrigger data-testid="compass-fw-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Criteria</Label>
              <Button variant="outline" size="sm" onClick={addCrit} data-testid="compass-fw-add-criterion">
                <Plus className="w-3.5 h-3.5" /> Add criterion
              </Button>
            </div>
            <div className="space-y-2">
              {form.criteria.map((c, i) => (
                <div key={c.id} className="p-3 rounded-lg border border-white/10 bg-white/[0.02] space-y-2"
                     data-testid={`compass-fw-criterion-${i}`}>
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-3.5 h-3.5 text-white/30" />
                    <Input placeholder="Criterion name (e.g. Judgment)" value={c.name}
                           onChange={(e) => setCrit(i, { name: e.target.value })}
                           data-testid={`compass-fw-crit-name-${i}`} />
                    <Input placeholder="Weight" type="number" step="0.5" className="w-24" value={c.weight}
                           onChange={(e) => setCrit(i, { weight: parseFloat(e.target.value) || 0 })}
                           data-testid={`compass-fw-crit-weight-${i}`} />
                    <Button variant="ghost" size="icon" onClick={() => rmCrit(i)}
                            data-testid={`compass-fw-crit-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                  <Textarea rows={1} placeholder="What does great look like here?"
                            value={c.description || ""}
                            onChange={(e) => setCrit(i, { description: e.target.value })}
                            data-testid={`compass-fw-crit-desc-${i}`} />
                  <div className="flex items-center gap-4 text-xs">
                    <label className="flex items-center gap-2" data-testid={`compass-fw-crit-musthave-${i}`}>
                      <Switch checked={!!c.must_have} onCheckedChange={(v) => setCrit(i, { must_have: v })} />
                      Must have
                    </label>
                    <label className="flex items-center gap-2" data-testid={`compass-fw-crit-redflag-${i}`}>
                      <Switch checked={!!c.red_flag} onCheckedChange={(v) => setCrit(i, { red_flag: v })} />
                      Red flag
                    </label>
                    <Input className="flex-1" placeholder="Evidence expected (comma-separated)"
                           value={(c.evidence_expectations || []).join(", ")}
                           onChange={(e) => setCrit(i, { evidence_expectations: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                           data-testid={`compass-fw-crit-evidence-${i}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Disqualifiers / Red flags</Label>
              <Button variant="outline" size="sm" onClick={addDisq} data-testid="compass-fw-add-disqualifier">
                <Plus className="w-3.5 h-3.5" /> Add disqualifier
              </Button>
            </div>
            <div className="space-y-2">
              {form.disqualifiers.map((d, i) => (
                <div key={d.id} className="p-2 rounded-lg border border-white/10 bg-white/[0.02] flex items-center gap-2">
                  <ShieldAlert className="w-3.5 h-3.5 text-red-300" />
                  <Input placeholder="Name (e.g. No relevant experience)" value={d.name}
                         onChange={(e) => set({ disqualifiers: form.disqualifiers.map((x, k) => k === i ? { ...x, name: e.target.value } : x) })} />
                  <Input placeholder="Description" value={d.description || ""}
                         onChange={(e) => set({ disqualifiers: form.disqualifiers.map((x, k) => k === i ? { ...x, description: e.target.value } : x) })} />
                  <Button variant="ghost" size="icon" onClick={() => rmDisq(i)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Strong Fit ≥</Label>
              <Input type="number" value={form.decision_thresholds.strong_fit}
                     onChange={(e) => set({ decision_thresholds: { ...form.decision_thresholds, strong_fit: parseInt(e.target.value) || 0 } })}
                     data-testid="compass-fw-threshold-strong" />
            </div>
            <div>
              <Label>Potential Fit ≥</Label>
              <Input type="number" value={form.decision_thresholds.potential_fit}
                     onChange={(e) => set({ decision_thresholds: { ...form.decision_thresholds, potential_fit: parseInt(e.target.value) || 0 } })}
                     data-testid="compass-fw-threshold-potential" />
            </div>
            <div>
              <Label>Weak Fit ≥</Label>
              <Input type="number" value={form.decision_thresholds.weak_fit}
                     onChange={(e) => set({ decision_thresholds: { ...form.decision_thresholds, weak_fit: parseInt(e.target.value) || 0 } })}
                     data-testid="compass-fw-threshold-weak" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compass-fw-cancel">Cancel</Button>
          <Button onClick={save} data-testid="compass-fw-save">
            {framework ? "Save" : "Create framework"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Evaluation Editor Dialog — scoring + evidence + reflection + alt fits
// ---------------------------------------------------------------------------
function EvaluationEditorDialog({ open, onClose, evaluationId, onChanged }) {
  const [ev, setEv] = useState(null);
  const [fw, setFw] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyDraft, setBusyDraft] = useState(false);
  const [alt, setAlt] = useState(null);
  const [altBusy, setAltBusy] = useState(false);
  const [tab, setTab] = useState("score");

  const load = useCallback(async () => {
    if (!evaluationId) return;
    const e = (await compassApi.getEvaluation(evaluationId)).data;
    const f = (await compassApi.getFramework(e.framework_id)).data;
    setEv(e);
    setFw(f);
  }, [evaluationId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const setScore = (criterion_id, patch) => {
    const arr = ev.criterion_scores || [];
    const idx = arr.findIndex((s) => s.criterion_id === criterion_id);
    const merged = idx >= 0
      ? arr.map((s, k) => k === idx ? { ...s, ...patch } : s)
      : [...arr, { criterion_id, score_0_5: null, met_status: "unclear", notes: "", ...patch }];
    setEv({ ...ev, criterion_scores: merged });
  };

  const commit = async () => {
    setSaving(true);
    try {
      const r = await compassApi.updateEvaluation(evaluationId, {
        criterion_scores: ev.criterion_scores,
        disqualifier_hits: ev.disqualifier_hits,
        strengths: ev.strengths, concerns: ev.concerns,
        open_questions: ev.open_questions,
        carry_forward: ev.carry_forward, next_interaction: ev.next_interaction,
      });
      setEv({ ...r.data });
      onChanged?.();
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const draft = async () => {
    setBusyDraft(true);
    try {
      const proposal = (await compassApi.llmDraft(evaluationId)).data;
      // Merge — LLM proposal is advisory; evaluator can edit any field.
      setEv((prev) => ({
        ...prev,
        criterion_scores: proposal.criterion_scores || prev.criterion_scores,
        strengths: [...(prev.strengths || []), ...(proposal.strengths || [])],
        concerns: [...(prev.concerns || []), ...(proposal.concerns || [])],
        open_questions: [...(prev.open_questions || []), ...(proposal.open_questions || [])],
        carry_forward: [
          ...(prev.carry_forward || []),
          ...(proposal.carry_forward || []).map((t) => ({ text: t })),
        ],
        next_interaction: [
          ...(prev.next_interaction || []),
          ...(proposal.next_interaction || []).map((t) => ({ id: `ni-${Math.random().toString(36).slice(2, 8)}`, text: t, resolved: false })),
        ],
      }));
      toast.success("AI-drafted — review and edit before saving");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "LLM draft failed — score manually");
    } finally { setBusyDraft(false); }
  };

  const loadAlt = async () => {
    setAltBusy(true);
    try {
      const r = (await compassApi.alternateFits(evaluationId, true)).data;
      setAlt(r.alternate_fits || []);
    } catch (e) {
      toast.error("Alternate-fit lookup failed");
    } finally { setAltBusy(false); }
  };

  const finalize = async () => {
    await commit();
    await compassApi.finalizeEvaluation(evaluationId);
    toast.success("Evaluation finalized");
    onChanged?.();
    onClose?.();
  };

  if (!ev || !fw) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
        <DialogContent className="max-w-4xl">Loading…</DialogContent>
      </Dialog>
    );
  }

  const scoresById = {};
  (ev.criterion_scores || []).forEach((s) => { scoresById[s.criterion_id] = s; });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="compass-evaluation-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <DialogTitle className="flex items-center gap-2">
              <Compass className="w-4 h-4" />
              {ev.target_display_name}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <RecPill rec={ev.recommendation} testid="compass-eval-recommendation" />
              <span className="text-[11px] text-white/50">Score {ev.weighted_score}/100 · Conf {(ev.confidence * 100).toFixed(0)}%</span>
            </div>
          </div>
          {ev.recommendation_locked_reason && (
            <p className="text-[11px] text-amber-300 mt-1" data-testid="compass-eval-locked-reason">
              <CircleAlert className="w-3 h-3 inline" /> {ev.recommendation_locked_reason}
            </p>
          )}
        </DialogHeader>

        <div className="flex items-center gap-2 mb-3">
          {["score", "evidence", "reflection", "altfit"].map((k) => (
            <button key={k}
                    onClick={() => setTab(k)}
                    data-testid={`compass-tab-${k}`}
                    className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded ${tab === k ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80"}`}>
              {k === "score" ? "Criteria" : k === "evidence" ? "Evidence" : k === "reflection" ? "Reflection" : "Alternate fits"}
            </button>
          ))}
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={draft} disabled={busyDraft}
                  data-testid="compass-btn-llm-draft">
            <Wand2 className="w-3.5 h-3.5" /> {busyDraft ? "Drafting…" : "AI-suggest"}
          </Button>
        </div>

        {tab === "score" && (
          <div className="space-y-3">
            {(fw.criteria || []).map((c) => {
              const s = scoresById[c.id] || {};
              const val = s.score_0_5;
              return (
                <div key={c.id} className="p-3 rounded-lg border border-white/10 bg-white/[0.02]"
                     data-testid={`compass-crit-row-${c.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white text-sm">{c.name}</span>
                        {c.must_have && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/30">Must have</span>}
                        {c.red_flag && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30">Red flag</span>}
                        <span className="text-[10px] text-white/40">weight {c.weight}</span>
                      </div>
                      {c.description && <p className="text-xs text-white/60 mt-1">{c.description}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input type="number" step="0.5" min="0" max="5" placeholder="0–5"
                             className="w-20"
                             value={val ?? ""}
                             onChange={(e) => setScore(c.id, { score_0_5: e.target.value === "" ? null : parseFloat(e.target.value) })}
                             data-testid={`compass-score-${c.id}`} />
                      <Select value={s.met_status || "unclear"}
                              onValueChange={(v) => setScore(c.id, { met_status: v })}>
                        <SelectTrigger className="w-32" data-testid={`compass-met-${c.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pass"><CheckCircle2 className="w-3 h-3 inline text-emerald-300" /> Pass</SelectItem>
                          <SelectItem value="fail"><CircleAlert className="w-3 h-3 inline text-red-300" /> Fail</SelectItem>
                          <SelectItem value="unclear"><HelpCircle className="w-3 h-3 inline text-white/50" /> Unclear</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Textarea rows={1} placeholder="What evidence supports this score?"
                            value={s.notes || ""}
                            onChange={(e) => setScore(c.id, { notes: e.target.value })}
                            className="mt-2"
                            data-testid={`compass-notes-${c.id}`} />
                </div>
              );
            })}

            {(fw.disqualifiers || []).length > 0 && (
              <div className="p-3 rounded-lg border border-red-500/20 bg-red-500/[0.03]">
                <div className="text-[11px] uppercase tracking-wider text-red-300 mb-2 flex items-center gap-1">
                  <ShieldAlert className="w-3.5 h-3.5" /> Disqualifiers
                </div>
                <div className="space-y-1.5">
                  {fw.disqualifiers.map((d) => {
                    const hit = (ev.disqualifier_hits || []).some((h) => h.disqualifier_id === d.id);
                    return (
                      <label key={d.id} className="flex items-center gap-2 text-sm"
                             data-testid={`compass-disq-${d.id}`}>
                        <Switch checked={hit} onCheckedChange={(v) => {
                          const next = v
                            ? [...(ev.disqualifier_hits || []), { disqualifier_id: d.id, name: d.name }]
                            : (ev.disqualifier_hits || []).filter((h) => h.disqualifier_id !== d.id);
                          setEv({ ...ev, disqualifier_hits: next });
                        }} />
                        <span>{d.name}</span>
                        {d.description && <span className="text-xs text-white/40">— {d.description}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "evidence" && (
          <EvidencePanel evaluationId={ev.id} evidence={ev.evidence || []} onChanged={load} />
        )}

        {tab === "reflection" && (
          <ReflectionPanel ev={ev} setEv={setEv} />
        )}

        {tab === "altfit" && (
          <AltFitPanel alt={alt} loadAlt={loadAlt} busy={altBusy} />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compass-eval-close">Close</Button>
          <Button variant="outline" onClick={commit} disabled={saving}
                  data-testid="compass-eval-save">Save</Button>
          {ev.status === "Draft" ? (
            <Button onClick={finalize} data-testid="compass-eval-finalize">Finalize</Button>
          ) : (
            <Button variant="outline" onClick={async () => { await compassApi.reopenEvaluation(evaluationId); load(); }}
                    data-testid="compass-eval-reopen">Reopen</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EvidencePanel({ evaluationId, evidence, onChanged }) {
  const [label, setLabel] = useState("");
  const [body, setBody] = useState("");
  const add = async () => {
    if (!body.trim()) return;
    await compassApi.addEvidence(evaluationId, { type: "note", label, body });
    setLabel(""); setBody("");
    onChanged?.();
  };
  return (
    <div className="space-y-3" data-testid="compass-evidence-panel">
      <div className="space-y-1.5">
        {evidence.map((e) => (
          <div key={e.id} className="p-2.5 rounded border border-white/10 bg-white/[0.02]"
               data-testid={`compass-evidence-row-${e.id}`}>
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-white/40">{e.type}{e.canonical_kind ? ` · ${e.canonical_kind}` : ""}</div>
                {e.label && <div className="text-sm text-white font-medium truncate">{e.label}</div>}
              </div>
              <Button variant="ghost" size="icon" onClick={async () => { await compassApi.deleteEvidence(e.id); onChanged?.(); }}>
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </Button>
            </div>
            {e.body && <p className="text-xs text-white/70 mt-1 whitespace-pre-wrap">{e.body}</p>}
            {e.url && <a href={e.url} target="_blank" rel="noreferrer" className="text-xs text-blue-300 underline">{e.url}</a>}
          </div>
        ))}
        {evidence.length === 0 && <div className="text-xs text-white/40 italic">No evidence yet.</div>}
      </div>
      <div className="pt-2 border-t border-white/10 space-y-2">
        <Input placeholder="Evidence label (optional)" value={label} onChange={(e) => setLabel(e.target.value)}
               data-testid="compass-evidence-label" />
        <Textarea rows={3} placeholder="Paste notes, quote, or observation…" value={body}
                  onChange={(e) => setBody(e.target.value)}
                  data-testid="compass-evidence-body" />
        <Button size="sm" onClick={add} data-testid="compass-evidence-add">
          <Plus className="w-3.5 h-3.5" /> Add evidence
        </Button>
      </div>
    </div>
  );
}

function ListEdit({ label, items, onChange, testid, placeholder }) {
  const [t, setT] = useState("");
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-[11px] uppercase tracking-wider text-white/60">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {(items || []).map((it, i) => {
          const text = typeof it === "string" ? it : it.text;
          return (
            <span key={i} className="text-[11px] px-2 py-1 rounded bg-white/[0.04] border border-white/10 text-white/80 flex items-center gap-1">
              {text}
              <button onClick={() => onChange(items.filter((_, k) => k !== i))} className="text-white/40 hover:text-red-300">×</button>
            </span>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Input value={t} onChange={(e) => setT(e.target.value)} placeholder={placeholder} />
        <Button size="sm" variant="outline" onClick={() => { if (t.trim()) { onChange([...(items || []), t]); setT(""); } }}>Add</Button>
      </div>
    </div>
  );
}

function ReflectionPanel({ ev, setEv }) {
  return (
    <div className="space-y-4" data-testid="compass-reflection-panel">
      <ListEdit label="Strengths" items={ev.strengths} onChange={(v) => setEv({ ...ev, strengths: v })}
                testid="compass-list-strengths" placeholder="e.g. Handles ambiguity well" />
      <ListEdit label="Concerns" items={ev.concerns} onChange={(v) => setEv({ ...ev, concerns: v })}
                testid="compass-list-concerns" placeholder="e.g. Sales experience unclear" />
      <ListEdit label="Open questions" items={ev.open_questions} onChange={(v) => setEv({ ...ev, open_questions: v })}
                testid="compass-list-questions" placeholder="e.g. Regulatory context in country?" />
      <ListEdit label="Carry forward — persists on the subject"
                items={(ev.carry_forward || []).map((c) => typeof c === "string" ? { text: c } : c)}
                onChange={(v) => setEv({ ...ev, carry_forward: v.map((c) => typeof c === "string" ? { text: c } : c) })}
                testid="compass-list-carry-forward"
                placeholder="e.g. Strong institutional partnership experience" />
      <ListEdit label="Next interaction — what to gather next"
                items={(ev.next_interaction || []).map((c) => typeof c === "string" ? { text: c } : c)}
                onChange={(v) => setEv({ ...ev, next_interaction: v.map((c) => typeof c === "string" ? { text: c, id: `ni-${Math.random().toString(36).slice(2, 8)}`, resolved: false } : c) })}
                testid="compass-list-next-interaction"
                placeholder="e.g. Ask for concrete municipal partnership example" />
    </div>
  );
}

function AltFitPanel({ alt, loadAlt, busy }) {
  return (
    <div className="space-y-3" data-testid="compass-altfit-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-white/60">
          Evidence-based candidates from other Projects' Active frameworks. Never manufactures — empty means no meaningful overlap.
        </p>
        <Button size="sm" variant="outline" onClick={loadAlt} disabled={busy}
                data-testid="compass-altfit-scan">
          {busy ? "Scanning…" : "Scan alternate fits"}
        </Button>
      </div>
      {alt === null && <div className="text-xs text-white/40 italic">Not scanned yet.</div>}
      {alt !== null && alt.length === 0 && <div className="text-xs text-white/40 italic">No alternate fits with sufficient evidence overlap.</div>}
      {(alt || []).map((c) => (
        <div key={c.framework_id} className="p-3 rounded-lg border border-white/10 bg-white/[0.02]"
             data-testid={`compass-altfit-${c.framework_id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-sm text-white font-medium">{c.target_project_name}</div>
              <div className="text-[11px] text-white/50">{c.target_entity_name} · {c.framework_name}</div>
            </div>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">
              Confidence {(c.confidence * 100).toFixed(0)}%
            </span>
          </div>
          {c.matched_criteria?.length > 0 && (
            <div className="text-[11px] text-white/60 mt-1">Matches: {c.matched_criteria.join(", ")}</div>
          )}
          {c.llm_reason && (
            <p className="text-xs text-white/70 mt-2 italic">{c.llm_reason}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick Create Framework Dialog — Simplification v2 (§1–§7).
// ---------------------------------------------------------------------------
// Two-pane flow:
//   Pane 1  →  one plain-language textarea + Analyze button.
//   Pane 2  →  short plain-language review (framework name, "Compass will
//              look at…", "Key reasons not to proceed…"), Save + Advanced.
// NO scoring config surfaces here — no weights, no must-have toggles, no
// red-flag switches, no threshold sliders, no Draft/Active picker. Save
// marks the framework Active immediately so it lands in Send to Compass
// and Evaluate Organization without a second decision. The Advanced
// button hands the exact same draft off to the existing
// FrameworkBuilderDialog for power users.
export function QuickCreateFrameworkDialog({ open, onClose, projectId,
                                       targetTypeHint = null,
                                       onSaved, onAdvanced }) {
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  useEffect(() => { if (open) { setBrief(""); setDraft(null); } }, [open]);

  const analyze = async () => {
    if (!brief.trim()) return toast.error("Describe what you're trying to evaluate");
    setBusy(true);
    try {
      const r = (await compassApi.draftFrameworkFromBrief(
        projectId, brief.trim(), targetTypeHint || undefined)).data;
      setDraft(r.draft);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compass could not draft this — try a more concrete brief");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      // Save Active on this project — user chose (b): only the current
      // project's framework is Active. Copies elsewhere remain Draft
      // (handled server-side by scoping to project_id).
      const saved = (await compassApi.createFramework(projectId, {
        ...draft, status: "Active",
      })).data;
      toast.success("Framework ready to use");
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const advanced = () => {
    // Hand the unpersisted draft to the advanced builder.  The builder
    // treats a payload without an `id` as create-mode, so every field
    // remains editable and Save still calls POST /frameworks.
    onAdvanced?.(draft || null);
    onClose?.();
  };

  const summary = draft?.plain_summary || null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="compass-quickcreate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-300" /> Quick Create Framework
          </DialogTitle>
        </DialogHeader>

        {!draft && (
          <div className="space-y-3" data-testid="compass-quickcreate-pane-input">
            <Label className="text-sm text-white/70">What are you trying to understand or evaluate?</Label>
            <Textarea
              rows={7}
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="e.g. Help me understand whether this organization is strategically useful to the Ethiopia Discovery and what kind of relationship or opportunity might exist."
              data-testid="compass-quickcreate-brief" />
            <p className="text-[11px] text-white/40">
              Compass builds the framework for you. You'll see a plain-language summary next — no toggles, no scoring settings.
            </p>
          </div>
        )}

        {draft && summary && (
          <div className="space-y-4" data-testid="compass-quickcreate-pane-review">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40">Framework</div>
              <div className="text-lg text-white font-semibold mt-0.5" data-testid="compass-quickcreate-review-name">
                {summary.framework_name}
              </div>
              <div className="text-xs text-white/50 mt-0.5" data-testid="compass-quickcreate-review-evaluating">
                Evaluating: {summary.evaluating_label}
              </div>
            </div>

            <div>
              <div className="text-sm text-white/80">{summary.intro}</div>
              <ul className="mt-1.5 space-y-1" data-testid="compass-quickcreate-review-criteria">
                {(summary.criteria_bullets || []).map((c, i) => (
                  <li key={i} className="text-sm text-white/90 flex items-start gap-2">
                    <span className="text-white/40 mt-0.5">•</span><span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            {(summary.reasons_bullets || []).length > 0 && (
              <div>
                <div className="text-sm text-white/80">{summary.reasons_intro}</div>
                <ul className="mt-1.5 space-y-1" data-testid="compass-quickcreate-review-reasons">
                  {summary.reasons_bullets.map((r, i) => (
                    <li key={i} className="text-sm text-red-200/90 flex items-start gap-2">
                      <ShieldAlert className="w-3.5 h-3.5 mt-0.5 text-red-300 shrink-0" /><span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!draft && (
            <>
              <Button variant="outline" onClick={onClose} disabled={busy}
                      data-testid="compass-quickcreate-cancel">Cancel</Button>
              <Button onClick={analyze} disabled={busy}
                      className="bg-white text-black hover:bg-white/90"
                      data-testid="compass-quickcreate-analyze">
                <Wand2 className="w-3.5 h-3.5 mr-1" /> {busy ? "Analyzing…" : "Analyze"}
              </Button>
            </>
          )}
          {draft && (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={busy}
                      data-testid="compass-quickcreate-back">Rewrite brief</Button>
              <Button variant="outline" onClick={advanced} disabled={busy}
                      data-testid="compass-quickcreate-advanced">Advanced</Button>
              <Button onClick={save} disabled={busy}
                      className="bg-white text-black hover:bg-white/90"
                      data-testid="compass-quickcreate-save">
                {busy ? "Saving…" : "Save Framework"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Compass Section on the Project workspace
// ---------------------------------------------------------------------------
export default function CompassSection({ projectId }) {
  const [frameworks, setFrameworks] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [openBuilder, setOpenBuilder] = useState(false);
  const [editFw, setEditFw] = useState(null);
  const [openQuick, setOpenQuick] = useState(false);
  const [openEval, setOpenEval] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showNewSubject, setShowNewSubject] = useState(false);
  const [newSubject, setNewSubject] = useState({ freeform_name: "", freeform_notes: "", framework_id: "" });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [fw, ev] = await Promise.allSettled([
        compassApi.listFrameworks(projectId),
        compassApi.listProjectEvaluations(projectId),
      ]);
      if (fw.status === "fulfilled") setFrameworks(fw.value.data || []);
      if (ev.status === "fulfilled") setEvaluations(ev.value.data || []);
      if (fw.status === "rejected" && fw.reason?.response?.status === 403) setError("forbidden");
    } catch (e) {
      setError(e?.response?.status || "error");
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => frameworks.filter((f) => f.status === "Active"), [frameworks]);
  const draft = useMemo(() => frameworks.filter((f) => f.status === "Draft"), [frameworks]);

  const createEvaluation = async () => {
    if (!newSubject.framework_id) return toast.error("Pick a framework");
    if (!newSubject.freeform_name.trim()) return toast.error("Subject name required");
    try {
      const fw = frameworks.find((f) => f.id === newSubject.framework_id);
      const r = (await compassApi.createEvaluation({
        project_id: projectId,
        framework_id: fw.id,
        target_type: fw.target_type,
        target_id: null,
        freeform_name: newSubject.freeform_name,
        freeform_notes: newSubject.freeform_notes,
      })).data;
      setShowNewSubject(false);
      setNewSubject({ freeform_name: "", freeform_notes: "", framework_id: "" });
      setOpenEval(r.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create evaluation");
    }
  };

  if (error === "forbidden") return null;

  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid={`compass-section-${projectId}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Compass className="w-3.5 h-3.5 text-white/60" />
          <h4 className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">Compass — Evaluations</h4>
          <span className="text-[10px] text-white/30">
            {frameworks.length} framework{frameworks.length === 1 ? "" : "s"} · {evaluations.length} evaluation{evaluations.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {frameworks.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => setShowNewSubject(true)}
                    data-testid={`compass-btn-new-eval-${projectId}`}>
              <Sparkles className="w-3.5 h-3.5" /> Evaluate a subject
            </Button>
          )}
          <Button size="sm" variant="outline"
                  onClick={() => setOpenQuick(true)}
                  className="rounded-full"
                  data-testid={`compass-btn-quick-create-${projectId}`}>
            <Zap className="w-3.5 h-3.5 text-amber-300" /> Quick Create
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setEditFw(null); setOpenBuilder(true); }}
                  data-testid={`compass-btn-new-framework-${projectId}`}
                  title="Advanced: build a framework criterion-by-criterion">
            <Plus className="w-3.5 h-3.5" /> Advanced
          </Button>
        </div>
      </div>

      {frameworks.length === 0 && (
        <p className="mt-2 text-xs text-white/40 italic" data-testid={`compass-empty-${projectId}`}>
          No evaluation framework yet — create one to start scoring candidates, organizations, or opportunities against consistent criteria.
        </p>
      )}

      {frameworks.length > 0 && (
        <div className="mt-3 space-y-2">
          {active.map((f) => (
            <div key={f.id} className="p-2.5 rounded-lg border border-white/10 bg-white/[0.02] flex items-center justify-between gap-2 flex-wrap"
                 data-testid={`compass-framework-row-${f.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-white">{f.name}</span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Active</span>
                  <span className="text-[10px] text-white/40">{f.target_type} · {(f.criteria || []).length} criteria</span>
                </div>
                {f.description && <p className="text-[11px] text-white/50 mt-0.5">{f.description}</p>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setEditFw(f); setOpenBuilder(true); }}>Edit</Button>
            </div>
          ))}
          {draft.map((f) => (
            <div key={f.id} className="p-2 rounded border border-dashed border-white/10 bg-white/[0.02] flex items-center justify-between gap-2">
              <div className="text-xs text-white/60">{f.name} · <span className="text-white/40">Draft</span></div>
              <Button size="sm" variant="ghost" onClick={() => { setEditFw(f); setOpenBuilder(true); }}>Edit</Button>
            </div>
          ))}
        </div>
      )}

      {evaluations.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid={`compass-evaluations-${projectId}`}>
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-white/50">Evaluations</div>
            {evaluations.length > 4 && (
              <button onClick={() => setExpanded((v) => !v)} className="text-[11px] text-white/60 hover:text-white flex items-center gap-1">
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {expanded ? "Collapse" : `See all ${evaluations.length}`}
              </button>
            )}
          </div>
          {(expanded ? evaluations : evaluations.slice(0, 4)).map((e) => (
            <button key={e.id} onClick={() => setOpenEval(e.id)}
                    className="w-full text-left p-2.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
                    data-testid={`compass-evaluation-row-${e.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium truncate">{e.target_display_name}</div>
                  <div className="text-[11px] text-white/40">
                    {e.status === "Finalized" ? "Finalized" : "Draft"} · Score {e.weighted_score}/100
                    {(e.next_interaction || []).filter((x) => !x.resolved).length > 0 &&
                      <> · <ArrowRight className="w-3 h-3 inline" /> {(e.next_interaction || []).filter((x) => !x.resolved).length} next</>}
                  </div>
                </div>
                <RecPill rec={e.recommendation} testid={`compass-evaluation-rec-${e.id}`} />
              </div>
            </button>
          ))}
        </div>
      )}

      {openBuilder && (
        <FrameworkBuilderDialog
          open={openBuilder} onClose={() => setOpenBuilder(false)}
          projectId={projectId} framework={editFw}
          onSaved={() => load()}
        />
      )}

      <QuickCreateFrameworkDialog
        open={openQuick} onClose={() => setOpenQuick(false)}
        projectId={projectId}
        onSaved={() => load()}
        onAdvanced={(draft) => {
          setEditFw(draft ? { ...draft } : null);
          setOpenBuilder(true);
        }}
      />

      {openEval && (
        <EvaluationEditorDialog
          open={!!openEval} onClose={() => setOpenEval(null)}
          evaluationId={openEval} onChanged={load}
        />
      )}

      <Dialog open={showNewSubject} onOpenChange={(v) => !v && setShowNewSubject(false)}>
        <DialogContent className="max-w-lg" data-testid="compass-new-subject-dialog">
          <DialogHeader><DialogTitle>Evaluate a subject</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Framework</Label>
              <Select value={newSubject.framework_id}
                      onValueChange={(v) => setNewSubject({ ...newSubject, framework_id: v })}>
                <SelectTrigger data-testid="compass-new-subject-framework"><SelectValue placeholder="Pick a framework" /></SelectTrigger>
                <SelectContent>
                  {[...active, ...draft].map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.name} · {f.target_type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject name (freeform allowed)</Label>
              <Input value={newSubject.freeform_name}
                     onChange={(e) => setNewSubject({ ...newSubject, freeform_name: e.target.value })}
                     placeholder="e.g. Sarah — Discovery Agent candidate"
                     data-testid="compass-new-subject-name" />
              <p className="text-[10px] text-white/40 mt-1">Freeform subjects can later be promoted to a canonical Contact.</p>
            </div>
            <div>
              <Label>Notes / evidence to start with</Label>
              <Textarea rows={4} value={newSubject.freeform_notes}
                        onChange={(e) => setNewSubject({ ...newSubject, freeform_notes: e.target.value })}
                        data-testid="compass-new-subject-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewSubject(false)}>Cancel</Button>
            <Button onClick={createEvaluation} data-testid="compass-new-subject-create">Start evaluation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
